/**
 * ScrollManager — SPA scroll behaviour, done right (rev 3 · Feb 2026).
 *
 * Bug being fixed: on browser back, the previous restore logic was firing
 * `window.scrollTo(y)` **before** the target page had finished fetching
 * its data. The page was often only a few hundred pixels tall at that
 * instant, so the browser clamped the scroll to whatever DOM height it
 * currently had — leaving the user parked at the (short) bottom of a
 * still-loading page. Then, as content grew below, they perceived it as
 * "landing at the bottom of the site".
 *
 * Guarantees:
 *   • On PUSH / REPLACE  → scroll to top instantly.
 *   • On POP (back/fwd)  → restore the previous position accurately, even
 *     when the target page's DOM height grows asynchronously. We retry
 *     the restore up to `RETRY_MS` while content settles, using a
 *     ResizeObserver + rAF loop so we never "miss" the target Y.
 *   • Position writes are debounced with rAF and skipped during our own
 *     programmatic scrollTo, so the target row never gets stomped on.
 *   • Uses `history.scrollRestoration = "manual"` so the browser's own
 *     restore logic never fights ours.
 *   • Scroll key is `pathname` only — the query-string is intentionally
 *     dropped so that filter changes on /archive don't create a new key.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const positions = new Map(); // pathname+search → last scrollY
const RETRY_MS = 4000;       // longest we'll wait for content to settle

// Opt out of browser scroll restoration IMMEDIATELY at module load — before
// React mounts and before the first popstate could fire. If we wait until
// a useEffect runs, the browser may already have started auto-restoring
// its cached scroll position, which conflicts with ours and produces the
// "landed a few thousand pixels below the target" symptom.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

export const ScrollManager = () => {
  const location = useLocation();
  const navigationType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const restoringRef = useRef(false);         // suppress save-during-restore

  // One-time: opt out of browser scroll restoration so we own the behaviour.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Save the current scroll position keyed by the LIVE window.location
  // pathname whenever the user actually scrolls. rAF-debounced.
  //
  // Why the live pathname (not the closure-captured location.pathname)?
  // When a route transition begins, React commits new content BEFORE it
  // runs useEffect cleanup. If the new page is shorter than the old one,
  // the browser clamps the viewport scroll and fires an intermediate
  // scroll event — while the OLD listener is still attached. Reading the
  // live pathname means that intermediate save is attributed to the NEW
  // route (harmless, immediately re-scrolled to 0 by the restore effect),
  // instead of stomping on the good scroll value we just saved for the
  // OLD route.
  //
  // Guarded by `restoringRef` so our own programmatic scrollTo during a
  // POP restore does not overwrite the value we're trying to restore.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (restoringRef.current) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const liveKey = window.location.pathname + window.location.search;
        positions.set(liveKey, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // On navigation: PUSH/REPLACE → top; POP → restore with retry loop.
  useEffect(() => {
    const key = location.pathname + location.search;

    if (navigationType !== "POP") {
      // Fresh forward navigation. Start at top.
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }

    const targetY = positions.get(key);
    if (typeof targetY !== "number" || targetY <= 0) {
      // Nothing to restore — sit where we are (browser default).
      return;
    }

    // Retry loop: keep scrolling to `targetY` while we're within the
    // RETRY_MS budget. Once we've reached the target once, we ENTER a
    // guard phase where every animation frame re-asserts the position
    // so nothing (async image loads, browser focus restoration,
    // snap-scroll drift) can silently move the user past their spot.
    restoringRef.current = true;
    const start = performance.now();
    let raf = 0;
    let ro = null;

    const attempt = () => {
      const maxY = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        0
      );
      const y = Math.min(targetY, maxY);
      // Only issue scrollTo when we've actually drifted — cheaper &
      // less likely to fight legitimate user input.
      if (Math.abs(window.scrollY - y) > 2) {
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
      }
      const elapsed = performance.now() - start;
      if (elapsed < RETRY_MS) {
        raf = requestAnimationFrame(attempt);
      } else {
        setTimeout(() => { restoringRef.current = false; }, 60);
        if (ro) { ro.disconnect(); ro = null; }
      }
    };

    // Observe body height changes as an additional trigger (image / fetch
    // load in). Complements the rAF retry loop for slow networks.
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        if (performance.now() - start < RETRY_MS) attempt();
      });
      ro.observe(document.body);
    }

    attempt();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      restoringRef.current = false;
    };
    // We intentionally only depend on the location key + navigationType;
    // a change to either means a new navigation event and a fresh restore
    // attempt. `location.pathname` and `location.search` are included so
    // filter-only URL changes are treated as new routes.
  }, [location.key, navigationType, location.pathname, location.search]);

  return null;
};

export default ScrollManager;
